<script setup lang="ts">
  import type { DialogCancelReason } from '@uplatform-ai/fe-utils'
  import type { ButtonProps } from 'naive-ui'

  import { UiDialog } from '@uplatform-ai/fe-utils'

  const props = withDefaults(
    defineProps<{
      ariaLabel?: string
      autoClose?: boolean
      bodyClass?: string
      cancelButtonProps?: Partial<ButtonProps>
      cancelLabel?: string
      closeClass?: string
      confirmButtonProps?: Partial<ButtonProps>
      confirmLabel?: string
      dialogClass?: string
      disable?: boolean
      footerClass?: string
      headerClass?: string
      loading?: boolean
      maxWidth?: string
      message?: string
      persistent?: boolean
      showCloseButton?: boolean
      simpleConfirm?: boolean
      title?: string
    }>(),
    {
      autoClose: true,
      cancelLabel: 'base_cancel',
      confirmLabel: 'base_yes',
      disable: false,
      loading: false,
      maxWidth: '480px',
      message: '',
      persistent: false,
      showCloseButton: true,
      simpleConfirm: false,
      title: '',
    },
  )

  const emit = defineEmits<{
    cancel: []
    close: []
    confirm: []
  }>()

  const isOpen = defineModel<boolean>('show', { default: false })

  const { t } = useI18n()

  const defaultCloseClass = 'absolute inset-e-0 top-1 z-10'
  const defaultHeaderClass = 'relative pe-8'
  const defaultBodyClass = 'pt-2 pb-6'
  const defaultFooterClass = 'flex flex-nowrap! items-center justify-end gap-1.5'
  const defaultDialogClass = 'overflow-hidden rounded-3xl! p-5'

  const resolvedCloseClass = computed(() => props.closeClass ?? defaultCloseClass)
  const resolvedDialogClass = computed(() => props.dialogClass ?? defaultDialogClass)
  const resolvedHeaderClass = computed(() => props.headerClass ?? defaultHeaderClass)
  const resolvedBodyClass = computed(() => {
    let bodyClass = props.bodyClass ?? defaultBodyClass
    if (props.simpleConfirm) {
      bodyClass = `${bodyClass} hidden`
    }
    return bodyClass
  })
  const resolvedFooterClass = computed(() => {
    let footerClass = props.footerClass ?? defaultFooterClass
    if (props.simpleConfirm) {
      footerClass = `${footerClass} justify-center! [&_button]:basis-full! [&_button]:shrink-1!`
    }
    return footerClass
  })
  const resolvedAriaLabel = computed(() => props.ariaLabel ?? (props.title ? t(props.title) : undefined))

  const handleCancel = () => {
    emit('cancel')
    isOpen.value = false
  }

  const handleConfirm = () => {
    emit('confirm')

    if (props.autoClose) {
      isOpen.value = false
    }
  }

  const handleUiDialogClose = (_reason: DialogCancelReason) => {
    emit('cancel')
    emit('close')
  }
</script>

<template>
  <UiDialog
    v-model:open="isOpen"
    :persistent="persistent"
    :max-width="maxWidth"
    :show-close-button="false"
    :aria-label="resolvedAriaLabel"
    :dialog-class="resolvedDialogClass"
    @close="handleUiDialogClose"
  >
    <template #header>
      <header :class="resolvedHeaderClass">
        <slot
          name="header"
          :close="handleCancel"
        >
          <h2
            v-if="title"
            class="heading-md text-heading! m-0"
            :class="{
              'mb-6! text-center': simpleConfirm,
            }"
          >
            {{ t(title) }}
          </h2>
        </slot>
        <div
          v-if="showCloseButton && !persistent"
          :class="resolvedCloseClass"
        >
          <NButton
            class="flex size-6 cursor-pointer items-center justify-center rounded-md border-none bg-transparent"
            :aria-label="t('base_close')"
            text
            @click="handleCancel"
          >
            <i
              class="fa-solid fa-xmark text-icon-placeholder text-base"
              aria-hidden="true"
            />
          </NButton>
        </div>
      </header>
    </template>

    <div :class="resolvedBodyClass">
      <slot name="body">
        <p
          v-if="message"
          class="body-lg wrap-break-word"
        >
          {{ t(message) }}
        </p>
      </slot>
    </div>

    <template #footer>
      <footer :class="resolvedFooterClass">
        <slot
          name="footer"
          :cancel="handleCancel"
          :close="handleCancel"
          :confirm="handleConfirm"
        >
          <NButton
            class="min-w-25!"
            round
            secondary
            size="medium"
            v-bind="cancelButtonProps"
            :disabled="loading"
            @click="handleCancel"
          >
            {{ t(cancelLabel!) }}
          </NButton>
          <NButton
            class="min-w-25!"
            round
            size="medium"
            type="primary"
            v-bind="confirmButtonProps"
            :disabled="loading || disable"
            :loading="loading"
            @click="handleConfirm"
          >
            {{ t(confirmLabel!) }}
          </NButton>
        </slot>
      </footer>
    </template>
  </UiDialog>
</template>
